// Multi-location (3CHA Kingston / 3CAL Albany) client plumbing.
//
// One codebase, one Supabase project, one `site` column — so a feature built
// once ships to both shops. This module owns three things:
//   1. the canonical location metadata (name + K/A badge),
//   2. which location is ACTIVE on this device, and
//   3. resolving the caller's role FOR that location.
//
// The active location is sent to Postgres as the `x-3c-site` request header
// (wired in supabaseClient.js). Server-side, auth_site() honours that header
// ONLY if the signed JWT's `sites` array already permits it — so this file is
// a selector, never a grant. Nothing here is a security boundary; editing
// localStorage by hand gets you exactly nowhere.

export const KINGSTON = 'kingston';
export const ALBANY = 'albany';

// Mirrors the `public.sites` table. Kept client-side rather than fetched so the
// shell can paint its location indicator on the first frame — a location badge
// that arrives late is worse than useless, because the window where you might
// act on the wrong shop is exactly the window before it renders.
// 🚨 `address` is CUSTOMER-FACING — it goes out on the pre-appointment email as
// the place to drive to. An empty string prints NOTHING; it must never fall
// back to the other shop's address, because the failure mode of a wrong one is
// a customer 60 miles away at the wrong door on the morning of their detail.
export const SITES = {
  [KINGSTON]: { slug: KINGSTON, name: '3C Kingston', short: 'K', app: '3CHA', accent: '#2563eb', gcPrefix: 'GC-',
    address: '454 NY-28, Kingston, NY 12401' },
  // Orange-600 rather than a brighter orange-500: the accent is also used as a
  // SOLID fill behind white text (the site swatch in the switcher and hub
  // tiles), and #f97316 doesn't carry white legibly. #ea580c reads in both
  // themes — against the dark canvas and against white cards in light mode.
  // ⚠️ The shop is branded Albany but SITS IN COLONIE — do not "correct" the
  // town to Albany to match the name. Confirmed by Ryder 2026-08-26; this is
  // the line a customer navigates by on the pre-appointment email.
  [ALBANY]:   { slug: ALBANY,   name: '3C Albany',   short: 'A', app: '3CAL', accent: '#ea580c', gcPrefix: 'GCA-',
    address: '14 Petra Ln, Colonie, NY 12205' },
};

export const SITE_ORDER = [KINGSTON, ALBANY];

export const siteMeta  = (slug) => SITES[slug] || SITES[KINGSTON];
export const siteName  = (slug) => siteMeta(slug).name;
export const siteShort = (slug) => siteMeta(slug).short;
// The shop's street address, for customer-facing copy. '' when we don't have
// one — the caller must omit the line rather than substitute anything.
export const siteAddress = (slug) => siteMeta(slug).address || '';
// Gift-card code series, one per shop. Kingston has run GC-### since before
// Albany existed, so Albany takes a distinct prefix rather than continuing
// the same numbers — two shops issuing into one series would collide, and
// `gift_cards_code_uq` now refuses that outright instead of letting two cards
// quietly share a code.
export const siteGcPrefix = (slug) => siteMeta(slug).gcPrefix || 'GC-';

const STORAGE_KEY = '3c_site';

// The active location for this device. Defaults to Kingston so every existing
// session — and every account with no site claims at all — behaves exactly as
// it did before this landed.
export function getActiveSite() {
  try { return localStorage.getItem(STORAGE_KEY) || KINGSTON; }
  catch (_) { return KINGSTON; }
}

export function storeActiveSite(slug) {
  try { localStorage.setItem(STORAGE_KEY, slug); } catch (_) {}
}

// --- JWT claim readers -----------------------------------------------------
// `sites` is the allow-list; `site` is the home location; `site_roles` is the
// optional {slug: role} map for accounts whose role DIFFERS per location (Ric
// is `hd` at Albany and `detailer` at Kingston off one login).

export function allowedSites(user) {
  const am = (user && user.app_metadata) || {};
  if (Array.isArray(am.sites) && am.sites.length) return am.sites.filter((s) => SITES[s]);
  return [am.site && SITES[am.site] ? am.site : KINGSTON];
}

export function homeSite(user) {
  const am = (user && user.app_metadata) || {};
  return SITES[am.site] ? am.site : KINGSTON;
}

// The effective role AT a location. Must stay in lockstep with the SQL
// auth_role() in 20260810120000_multisite_foundation.sql — if these two ever
// disagree, the UI shows one profile while RLS enforces another, which is the
// most confusing possible failure mode.
export function roleForSite(user, slug) {
  const am = (user && user.app_metadata) || {};
  if (am.site_roles && am.site_roles[slug]) return am.site_roles[slug];
  return am.role || null;
}

// Clamp a requested location to what this account may actually enter. Called on
// every session sync, so a stale localStorage value from a previous login (or
// an account that lost access) can't leave the UI pointed at a shop the JWT
// will refuse to serve.
export function resolveSite(user, requested) {
  const allowed = allowedSites(user);
  if (requested && allowed.includes(requested)) return requested;
  const home = homeSite(user);
  return allowed.includes(home) ? home : allowed[0];
}

// Switching location is a FULL RELOAD, deliberately.
//
// api.js keeps an in-memory read cache (apiCached) and every mounted page holds
// location-specific state — bookings, customer lists, stock rows. Swapping the
// header without tearing all of that down would leave Kingston's data on screen
// under an Albany label, which is precisely the mistake this whole feature has
// to make impossible. The reload is the guarantee, and it is not negotiable.
//
// It DOES stay where you are, though. `to` defaults to the current path, so
// switching shops from the Overview lands you on the other shop's Overview —
// straight across, never back out to 3CLHUB. The dashboard's active tab lives
// in sessionStorage ('activeTab'), which survives a reload, so the specific tab
// is preserved too, not just the route.
//
// Only an explicit `to` sends you elsewhere — the 3CLHUB tiles pass one,
// because there the location change IS the navigation.
export function switchSite(slug, { to } = {}) {
  storeActiveSite(slug);
  // Anything cached WITHOUT a location in its key has to go, or the reload
  // repaints the shop you just left. The main api cache is namespaced per site
  // (see apiLegacy PERSIST_KEY); these two aren't:
  //   3c_warm_overview — a pre-warmed dashboard payload from the legacy hub
  //   3c_sched_open    — a booking id that doesn't exist at the other shop
  try {
    sessionStorage.removeItem('3c_warm_overview');
    sessionStorage.removeItem('3c_sched_open');
  } catch (_) {}
  const here = (() => {
    try { return window.location.pathname + window.location.search; }
    catch (_) { return '/'; }
  })();
  const target = to || here;
  try {
    // Assigning the identical URL does reload, but reload() is the explicit
    // form and avoids any browser quirk around same-URL assignment.
    if (target === here) window.location.reload();
    else window.location.assign(target);
  } catch (_) {
    try { window.location.reload(); } catch (_) {}
  }
}

// ===========================================================================
// THE GATE — you are in one shop or the other, and the label is proven.
// ===========================================================================
// Ryder, 2026-08-22: "i need a real gate between albany and kingston.
// sometimes the tag at the top says one location but gives me info from the
// other. it needs to be very securely one or the other."
//
// Isolation at the DATABASE was never the problem — RLS scopes every read to
// auth_site(), and a forged header cannot widen it past the JWT's own `sites`
// list. The problem was entirely on this side: the client could BELIEVE it
// was in one shop while its requests said the other, and then label the
// answers with the wrong shop. Two mechanisms close that.

// (a) Nothing may change the active shop underneath a running page. Another
// tab, another window, or the installed PWA writing `3c_site` used to leave
// this one rendering the previous shop with the new label. localStorage
// `storage` events only fire in OTHER documents, which is exactly the case
// that needs catching. Reloading is the same remedy switchSite() already
// uses, and for the same reason: in-memory caches cannot be trusted across a
// shop change.
export function watchSiteChanges() {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e) => {
    if (e.key !== STORAGE_KEY) return;
    if (!e.newValue || e.newValue === e.oldValue) return;
    try { window.location.reload(); } catch (_) {}
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

// (b) Ask the SERVER which shop it is answering as, and refuse to disagree
// with it. auth_site() is the exact function every RLS policy consults, so
// its answer is the shop the data actually came from — not what this client
// hoped it asked for. If they differ, the label is wrong and the honest move
// is to adopt the truth and reload rather than keep painting the wrong tag.
//
// ⚠️ Guarded against a reload loop: it corrects at most once per tab. If a
// mismatch somehow survives the reload, it reports rather than cycling —
// an app stuck in a refresh loop is worse than a wrong badge.
const RECONCILED_KEY = '3c_site_reconciled';
export async function reconcileSiteWithServer(supabase, allowed) {
  try {
    const { data, error } = await supabase.rpc('auth_site');
    if (error) return null;
    const serverSite = String(data || '').trim();
    if (!serverSite) return null;
    const localSite = getActiveSite();
    if (serverSite === localSite) {
      try { sessionStorage.removeItem(RECONCILED_KEY); } catch (_) {}
      return serverSite;
    }
    // Only adopt something this account is actually allowed into, or a
    // surprising answer could park the UI on a shop it can never read.
    if (Array.isArray(allowed) && allowed.length && !allowed.includes(serverSite)) {
      return serverSite;
    }
    let already = false;
    try { already = sessionStorage.getItem(RECONCILED_KEY) === '1'; } catch (_) {}
    if (already) return serverSite; // reported, not looped
    try { sessionStorage.setItem(RECONCILED_KEY, '1'); } catch (_) {}
    storeActiveSite(serverSite);
    try { window.location.reload(); } catch (_) {}
    return serverSite;
  } catch (_) { return null; }
}
